<template>
    <section class="home-news" aria-labelledby="home-news-title">
        <div class="home-news__container">
            <div class="home-news__panel">
                <div class="home-news__media">
                    <img
                        class="home-news__image"
                        :src="homeNewsImageUrl"
                        width="332"
                        height="188"
                        alt="查看最新消息"
                    />
                </div>
                <div class="home-news__content">
                    <header class="home-news__header">
                        <h2 id="home-news-title" class="home-news__heading">
                            最新消息
                        </h2>
                        <a
                            href="/Home/Events?b=new_00"
                            class="home-news__more-link"
                        >
                            <span class="home-news__more-text">更多資訊</span>
                            <span
                                class="home-news__more-arrow"
                                aria-hidden="true"
                            ></span>
                        </a>
                    </header>
                    <ul class="home-news__list">
                        <li
                            v-for="news in newsList"
                            :key="String(news.id || news.title || '')"
                            class="home-news__item"
                        >
                            <a
                                class="home-news__item-link"
                                :href="getNewsUrl(news.newsMonth)"
                            >
                                <time
                                    class="home-news__date"
                                    :datetime="news.dateString"
                                >
                                    {{ formatNewsDate(news.dateString) }}
                                </time>
                                <span class="home-news__title">
                                    {{ stripHtml(news.title) }}
                                </span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import { getHomeLatestNews } from '@/api/home/home-news.api';
    import type { HomeNewsItem } from '@/types/home/home-news.types';
    import { HOME_LATEST_NEWS_IMAGE_URL } from './home-index.shared';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            initialNewsList?: HomeNewsItem[];
        }>(),
        {
            initialNewsList: () => [],
        }
    );

    // ----- state -----
    const newsList = ref<HomeNewsItem[]>([...props.initialNewsList]);
    const homeNewsImageUrl = HOME_LATEST_NEWS_IMAGE_URL;

    // ----- methods -----
    const fetchLatestNews = async (): Promise<void> => {
        try {
            newsList.value = await getHomeLatestNews();
        } catch {
            newsList.value = [];
        }
    };

    const getNewsUrl = (month?: number): string => {
        const normalizedMonth =
            month && month >= 1 && month <= 12
                ? month.toString().padStart(2, '0')
                : '00';

        return `/Home/Events?b=new_${normalizedMonth}`;
    };

    const stripHtml = (value?: string): string => {
        return String(value || '').replace(/<[^>]*>/g, '');
    };

    const formatNewsDate = (dateString?: string): string => {
        return String(dateString || '').replace(/-/g, '.');
    };

    // ----- lifecycle -----
    onMounted(() => {
        if (window.__GH_SSR_MODE__) {
            return;
        }

        void fetchLatestNews();
    });
</script>

<style lang="scss" scoped>
    .home-news {
        width: 100%;

        &__container {
            width: $contentOuter;
            margin: 0 auto;
            transition: width 0.3s ease-out;
        }

        &__panel {
            position: relative;
            width: 100%;
            padding: $spacing-lg $spacing-md;
            display: flex;
            border: 1px solid $grayColorE7;
            border-radius: $border-radius-md;
        }

        &__media {
            width: 30%;
            padding-right: $spacing-md;
        }

        &__image {
            width: 100%;
            height: auto;
            display: block;
            border: 1px solid $grayColorE7;
            border-radius: $border-radius-md;
        }

        &__content {
            width: 70%;
        }

        &__header {
            width: 100%;
            padding: $spacing-md 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid $textColor45;
        }

        &__heading {
            margin: 0;
            color: $textColor45;
            font-size: $font-size-lg;
            font-weight: normal;
            line-height: 1.5rem;
        }

        &__more-link {
            margin-right: $spacing-sm;
            display: flex;
            position: relative;
            align-items: center;
            color: $textColor888;
            font-size: $font-size-sm;
            line-height: 1.5rem;

            &:hover {
                color: $blueColor_2C94FF;

                .home-news__more-arrow {
                    border-color: $blueColor_2C94FF;
                }
            }
        }

        &__more-text {
            margin-right: $spacing-sm;
            margin-bottom: -2px;
        }

        &__more-arrow {
            position: relative;
            width: 8px;
            height: 8px;
            padding: 3px;
            display: inline-block;
            border: solid $textColor888;
            border-width: 1px 1px 0 0;
            transform: rotate(45deg);
        }

        &__list {
            margin: 0;
            padding: 0;
            list-style: none;
        }

        &__item {
            padding-top: $spacing-md;
            font-size: $font-size-sm;
            line-height: 1.2rem;
        }

        &__item-link {
            display: flex;
            align-items: center;
            color: $textColor888;

            &:hover {
                color: $blueColor_2C94FF;
            }
        }

        &__date {
            flex: 0 0 auto;
            display: inline-block;
        }

        &__title {
            width: 89%;
            display: inline-block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        @include rwdL($fullHD) {
            &__container {
                width: $contentOuterHD;
            }
        }

        @include rwd($fullHD) {
            &__container {
                width: 1000px;
            }
        }

        @include rwd($contentOuterHD) {
            &__container {
                width: 80%;
            }
        }

        @include rwd($pc + 10) {
            &__container {
                width: 90%;
            }
        }

        @include rwd($mobileM) {
            &__container {
                width: 100%;
            }

            &__panel {
                flex-direction: column;
                align-items: center;
                border-radius: 0;
                text-align: center;
            }

            &__media {
                width: 80%;
                padding: 0;
            }

            &__content {
                width: 95%;
            }
        }
    }
</style>
